/**
 * Encrypted artifact index: every endpoint that uploads an artifact (vikod, or this browser's
 * attachment upload) also writes a sealed `artifact` entity. The Artifacts tab lists those
 * entities; the relay only holds opaque heads, so there is no plaintext index anywhere.
 */
import { ArtifactIndexEntry, PrivateEntityId } from '@viko/protocol'
import type { BrowserEntities } from './encrypted-entities.js'
import type { Artifact } from './types.js'

type Entities = Pick<BrowserEntities, 'list' | 'write'>

/** `art_<uuid>` → the entity id, so a retried record rewrites the same entity. */
export function artifactEntityId(objectId: string): string | undefined {
  const id = objectId.startsWith('art_') ? objectId.slice(4) : ''
  return PrivateEntityId.safeParse(id).success ? id : undefined
}

export async function recordArtifactIndex(entities: Entities, raw: ArtifactIndexEntry) {
  const entry = ArtifactIndexEntry.parse(raw)
  const id = artifactEntityId(entry.ref.objectId)
  if (!id) throw new Error('Artifact index needs an art_<uuid> object id')
  await entities.write('artifact', id, entry)
}

export async function listIndexedArtifacts(
  entities: Pick<BrowserEntities, 'list'>,
  accountId: string,
  q: { projectId?: string; taskId?: string; runId?: string } = {},
  runs: readonly { runId: string; projectId?: string | undefined }[] = [],
): Promise<Artifact[]> {
  const runProject = new Map(runs.map((r) => [r.runId, r.projectId]))
  const out: Artifact[] = []
  for (const row of await entities.list('artifact')) {
    const parsed = ArtifactIndexEntry.safeParse(row.value)
    // Authenticated but unusable (another account's reference, or a newer shape): skip it.
    if (!parsed.success || parsed.data.ref.accountId !== accountId) continue
    const e = parsed.data
    if (e.thumb && e.thumb.accountId !== accountId) continue
    const projectId = e.projectId ?? runProject.get(e.runId)
    if (q.projectId && projectId !== q.projectId) continue
    if (q.taskId && e.taskId !== q.taskId) continue
    if (q.runId && e.runId !== q.runId) continue
    out.push({
      id: e.ref.objectId,
      runId: e.runId,
      projectId: projectId ?? null,
      taskId: e.taskId ?? null,
      ideaId: null,
      name: e.name,
      title: null,
      kind: null,
      caption: e.caption ?? null,
      group: null,
      mime: e.mime,
      bytes: e.size,
      sha256: null,
      uploaded: true,
      createdAt: e.createdAt,
      ...(e.workstreamId ? { workstreamId: e.workstreamId } : {}),
      ...(e.thumb ? { thumbId: e.thumb.objectId } : {}),
      encrypted: e.ref,
      ...(e.thumb ? { thumbEncrypted: e.thumb } : {}),
      // auto-artifacts
      ...(e.source ? { source: e.source } : {}),
      ...(e.collection ? { collection: e.collection } : {}),
      ...(e.external ? { external: e.external } : {}),
      ...(e.bundle ? { bundle: true } : {}),
      // visual-review
      ...(e.provenance ? { provenance: e.provenance } : {}),
      ...(e.sidecar ? { sidecar: e.sidecar } : {}),
    })
  }
  return out.sort((a, b) => b.createdAt - a.createdAt)
}
